import React, { useState, useEffect, useContext } from 'react';
import Header from '../components/Header';
import { FaMoneyBill, FaPlusCircle, FaTimes, FaUniversity } from 'react-icons/fa';
import { create_withdrawal_api, get_artist_withdraw_requests_api } from '../controllers/dataController';
import { AuthContext } from '../context/AuthContext';
import { AlertContext } from '../context/AlertContext';
import {Button} from '../components/custom_ui/button.tsx';

// Modal to display bank details
const BankDetailsModal = ({ request, onClose }) => {
    if (!request) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50" onClick={onClose}>
            <div className="bg-gradient-card border border-border/50 rounded-lg p-8 max-w-md w-full shadow-xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">Bank Details for Request #{request.id}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <FaTimes size={20} />
                    </button>
                </div>
                <div className="space-y-4 text-gray-300">
                    <p><strong>Account Holder:</strong> {request.account_holder_name}</p>
                    <p><strong>Account Number:</strong> {request.account_number}</p>
                    <p><strong>Bank Name:</strong> {request.bank_name}</p>
                    <p><strong>IFSC Code:</strong> {request.ifsc_code}</p>
                    <p><strong>Account Type:</strong> <span className="capitalize">{request.account_type?.toLowerCase()}</span></p>
                    <p><strong>Transaction ID:</strong> {request.transaction_id || 'N/A'}</p>
                </div>
            </div>
        </div>
    );
};


const getStatusClasses = (status) => {
    switch (status) {
        case 'APPROVED': return 'bg-green-600 text-white';
        case 'REJECTED': return 'bg-red-600 text-white';
        case 'PENDING': return 'bg-yellow-600 text-white';
        default: return 'bg-gray-500 text-white';
    }
};

const WithdrawRequestArtistPage = () => {
    const { artist } = useContext(AuthContext);
    const { showAlert } = useContext(AlertContext);
    const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
    const [requests, setRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);

    const [newWithdrawalForm, setNewWithdrawalForm] = useState({
        amount: '',
        account_holder_name: '',
        account_number: '',
        bank_name: '',
        ifsc_code: '',
        account_type: 'SAVING',
    });

    useEffect(() => {
        if (artist) {
            setNewWithdrawalForm(prev => ({
                ...prev,
                account_holder_name: artist.account_holder_name || '',
                account_number: artist.account_number || '',
                bank_name: artist.bank_name || '',
                ifsc_code: artist.ifsc_code || '',
                account_type: artist.account_type || 'SAVING',
            }));
        }
    }, [artist]);

    const fetchWithdrawalRequests = () => {
        get_artist_withdraw_requests_api()
            .then(response => {
                setRequests(response.data);
            })
            .catch(error => {
                console.error("Failed to get withdrawal requests:", error);
            });
    };

    useEffect(() => {
        fetchWithdrawalRequests();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewWithdrawalForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateWithdrawal = () => {
        const amount = parseFloat(newWithdrawalForm.amount);
        if (isNaN(amount) || amount < 10000) {
            showAlert("Minimum withdrawal amount is ₹10,000.", "danger");
            return;
        }

        create_withdrawal_api(newWithdrawalForm)
            .then(() => {
                showAlert("Withdrawal request submitted successfully.", "success");
                setShowWithdrawalForm(false);
                fetchWithdrawalRequests(); // Refresh the list
            })
            .catch((error) => {
                showAlert(error || "Failed to create withdrawal.", "danger");
                console.error("Failed to create withdrawal:", error);
            });
    };
    
    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setShowBankDetailsModal(true);
    };

    const tableHeaders = ["ID", "Amount", "Status", "Requested At", "Bank Details"];

    return (
        <div className='bg-background'>
            <div className='absolute top-0 left-0 right-0'>
                <Header />
            </div>
            <div className="min-h-screen text-white pt-28 py-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-card border border-border/50 rounded-lg shadow-xl p-6 mb-8">
                        <div className="flex justify-between items-center mb-16">
                            <h3 className="text-2xl font-bold text-white">
                                <FaMoneyBill className="inline-block mr-3 text-green-400" />
                                Withdrawal Requests
                            </h3>
                            <Button
                                className="bg-gradient-to-r from-purple-600 to-blue-500 text-white font-bold py-2 px-4 rounded-md hover:from-purple-700 hover:to-blue-600 transition duration-200 flex items-center"
                                onClick={() => setShowWithdrawalForm(!showWithdrawalForm)}
                            >
                                {showWithdrawalForm ? <FaTimes className="mr-2" /> : <FaPlusCircle className="mr-2" />}
                                {showWithdrawalForm ? 'Cancel' : 'Create New Request'}
                            </Button>
                        </div>

                        {showWithdrawalForm && (
                            <div className="mt-6 p-6 bg-gradient-card border border-border/50 rounded-lg shadow-inner">
                                <h4 className="text-xl font-semibold mb-4 text-center text-gray-200">New Withdrawal Request</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Amount */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="amount">Amount (Min ₹10,000)</label>
                                        <input id="amount" name="amount" type='number' value={newWithdrawalForm.amount} onChange={handleInputChange} placeholder="Enter amount (Min 10,000)" className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500" />
                                    </div>
                                    {/* Account Holder Name */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="account_holder_name">Account Holder Name</label>
                                        <input id="account_holder_name" name="account_holder_name" type='text' value={newWithdrawalForm.account_holder_name} onChange={handleInputChange} placeholder="Enter account holder name" className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500" />
                                    </div>
                                    {/* Account Number */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="account_number">Account Number</label>
                                        <input id="account_number" name="account_number" type='text' value={newWithdrawalForm.account_number} onChange={handleInputChange} placeholder="Enter account number" className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500" />
                                    </div>
                                    {/* Bank Name */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="bank_name">Bank Name</label>
                                        <input id="bank_name" name="bank_name" type='text' value={newWithdrawalForm.bank_name} onChange={handleInputChange} placeholder="Enter bank name" className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500" />
                                    </div>
                                    {/* IFSC Code */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="ifsc_code">IFSC Code</label>
                                        <input id="ifsc_code" name="ifsc_code" type='text' value={newWithdrawalForm.ifsc_code} onChange={handleInputChange} placeholder="Enter IFSC code" className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500" />
                                    </div>
                                    {/* Account Type */}
                                    <div>
                                        <label className="block text-gray-400 text-sm mb-2" htmlFor="account_type">Account Type</label>
                                        <select id="account_type" name="account_type" value={newWithdrawalForm.account_type} onChange={handleInputChange} className="w-full bg-gray-800 border border-gray-600 text-white rounded-md py-2 px-3 focus:outline-none focus:border-purple-500">
                                            <option value="SAVING">Saving</option>
                                            <option value="CURRENT">Current</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-6 text-center">
                                    <Button variant={"glow"} onClick={handleCreateWithdrawal} className="text-white font-bold py-2 px-8 rounded-md ">
                                        Submit Request
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-gradient-card border border-border/50 rounded-lg shadow-xl overflow-x-auto">
                        <h3 className="text-xl font-bold text-white p-6">Your Requests</h3>
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-700">
                                <tr>
                                    {tableHeaders.map(header => <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{header}</th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {requests.length > 0 ? requests.map((request) => (
                                    <tr key={request.id} className="hover:bg-gray-700 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-300">{request.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-400">₹{parseFloat(request.amount).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClasses(request.status)}`}>{request.status}</span></td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{new Date(request.requested_at).toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button onClick={() => handleViewDetails(request)} className="text-indigo-400 hover:text-indigo-600 font-semibold flex items-center">
                                                <FaUniversity className="mr-2" /> View Details
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={tableHeaders.length} className="px-6 py-12 text-center text-gray-500">You have not made any withdrawal requests yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showBankDetailsModal && <BankDetailsModal request={selectedRequest} onClose={() => setShowBankDetailsModal(false)} />}
        </div>
    );
};

export default WithdrawRequestArtistPage;